import React from "react";
import { Formik, Form } from "formik";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router";

import ScannerInput from "../../form/ScannerInput";
import ItemsAlreadyMoved from "./ItemsAlreadyMoved";
import useQueryParams from "src/hooks/useQueryParams";

const SEARCH_MODES = {
  exact: "exact",
  broad: "broad",
};

const inputStyles = {
  matchModeAdornment: {
    alignSelf: "stretch",
    height: "auto",
    maxHeight: "none",
    ml: 0.5,
    mr: 0.5,
    pr: 0.5,
    borderRight: "1px solid",
    borderColor: "divider",
  },
  matchModeSelect: {
    width: "auto",
    height: "100%",
    fontSize: 14,
    fontWeight: 400,
    color: "text.primary",
    "& .MuiSelect-select": {
      display: "flex",
      alignItems: "center",
      py: 0,
      pl: 0,
      pr: "18px !important",
      minWidth: "0 !important",
    },
    "& .MuiSelect-icon": {
      color: "primary.main",
      right: 0,
      fontSize: 20,
    },
  },
};

export default function ManualSearchModal({ open, onClose, onSearch }) {
  const navigate = useNavigate();
  const params = useQueryParams();
  const movedAssetIds = params.get("movedAssetIds");
  const rapidScanEnabled = params.get("rapidScan") === "true";

  const setRapidScanEnabled = enabled => {
    if (enabled) {
      params.set("rapidScan", "true");
    } else {
      params.delete("rapidScan");
    }
    navigate({ search: params.toString() });
  };

  const handleSubmit = values => {
    if (values.code.trim()) {
      onSearch(values.code, values.matchMode);
    }
  };

  const handleDetected = (code, matchMode) => {
    if (code.trim()) {
      onSearch(code, matchMode);
    }
  };

  const handleComplete = () => {
    onClose(true);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
        <Typography fontSize={22} fontWeight={500} color="textPrimary" align="center">
          Inventory Move Search
        </Typography>
        <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ minHeight: 140, maxHeight: "70vh", overflow: "auto" }}>
        <Formik initialValues={{ code: "", matchMode: SEARCH_MODES.exact }} onSubmit={handleSubmit}>
          {({ values, setFieldValue }) => (
            <Form>
              <Box sx={{ mt: 2 }}>
                <ScannerInput
                  autoFocus
                  size="large"
                  name="code"
                  label="What Would You Like to Move?"
                  showNAButton={false}
                  onDetected={code => handleDetected(code, values.matchMode)}
                  scanContainerProps={{
                    justifyContent: "flex-start",
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={inputStyles.matchModeAdornment}>
                        <Select
                          name="matchMode"
                          value={values.matchMode}
                          onChange={event => setFieldValue("matchMode", event.target.value)}
                          renderValue={value => value === SEARCH_MODES.exact ? "Exact" : "Similar"}
                          variant="standard"
                          disableUnderline
                          sx={inputStyles.matchModeSelect}
                        >
                          <MenuItem value={SEARCH_MODES.exact}>Exact Match</MenuItem>
                          <MenuItem value={SEARCH_MODES.broad}>Similar Match</MenuItem>
                        </Select>
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton type="submit" disabled={!values.code.trim()}>
                          <SearchIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Form>
          )}
        </Formik>
        {movedAssetIds && (
          <Box display="flex" justifyContent="flex-end" mt={0.5}>
            <FormControlLabel
              control={
                <Switch
                  checked={rapidScanEnabled}
                  onChange={e => setRapidScanEnabled(e.target.checked)}
                />
              }
              label="Rapid Scan"
            />
          </Box>
        )}
        <ItemsAlreadyMoved />
        {movedAssetIds && (
          <Box display="flex" justifyContent="flex-end" sx={{ mt: 3, pb: 2 }}>
            <Button type="button" variant="contained" color="primary" onClick={handleComplete}>
              Done Moving to This Location
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
